import { DOCUMENT } from "@angular/common";
import {
  ApplicationRef,
  ComponentFactoryResolver,
  ComponentRef,
  EmbeddedViewRef,
  FactoryProvider,
  Inject,
  Injectable,
  Injector,
  NgZone,
  Optional,
  SkipSelf,
  StaticProvider,
  TemplateRef,
  Type,
  ViewContainerRef
} from "@angular/core";

import { OverlayContainerService } from "./overlay-container.service";
import { OverlayContext } from "./overlay-context";
import { ComponentOverlayRef, OverlayRef, ViewOverlayRef } from "./overlay-ref";
import { PositionStrategy } from "./position-strategies";

@Injectable()
export class OverlayService {
  private documentRef: Document;
  private overlayRefs: OverlayRef[] = [];

  constructor(
    @Inject(DOCUMENT) document: any,
    private overlayContainerService: OverlayContainerService,
    private applicationRef: ApplicationRef,
    private componentFactoryResolver: ComponentFactoryResolver,
    private injector: Injector,
    private ngZone: NgZone
  ) {
    this.documentRef = document;
  }

  createComponent<C>(
    componentType: Type<C>,
    positionStrategy: PositionStrategy,
    injector: Injector = this.injector,
    providers:
      | StaticProvider[]
      | ((overlayRef: ComponentOverlayRef<C>) => StaticProvider[]) = []
  ): ComponentOverlayRef<C> {
    const elem: HTMLDivElement = this.createOverlayElement();
    const overlayRef = new ComponentOverlayRef<C>(
      elem,
      positionStrategy,
      this.documentRef.defaultView!,
      this.ngZone
    );

    if (typeof providers === "function") {
      providers = providers(overlayRef);
    }

    const childInjector: Injector = Injector.create({
      providers: [
        ...providers,
        {
          provide: OverlayRef,
          useValue: overlayRef
        }
      ],
      parent: injector
    });

    const factory =
      this.componentFactoryResolver.resolveComponentFactory(componentType);
    const componentRef: ComponentRef<C> = factory.create(childInjector);
    this.applicationRef.attachView(componentRef.hostView);
    elem.appendChild(this.getComponentRootNode(componentRef));
    componentRef.changeDetectorRef.detectChanges();

    overlayRef.componentRef = componentRef;
    this.overlayRefs.push(overlayRef);
    overlayRef.setDestroyFn(() => {
      let i: number = this.overlayRefs.indexOf(overlayRef);
      if (!!~i) {
        this.overlayRefs.splice(i, 1);
      }

      this.applicationRef.detachView(componentRef.hostView);
      componentRef.destroy();
    });

    overlayRef.init();

    return overlayRef;
  }

  createTemplate<C extends Exclude<any, () => any>>(
    templateRef: TemplateRef<OverlayContext<C>>,
    viewContainerRef: ViewContainerRef,
    positionStrategy: PositionStrategy,
    context?: C | ((overlayRef: ViewOverlayRef<C>) => C)
  ): ViewOverlayRef<C> {
    const elem: HTMLDivElement = this.createOverlayElement();
    const overlayRef = new ViewOverlayRef<C>(
      elem,
      positionStrategy,
      this.documentRef.defaultView!,
      this.ngZone
    );

    const destroy: () => void = () => overlayRef.destroy();
    let childContext: OverlayContext<C>;
    if (context instanceof Function) {
      childContext = Object.assign({}, context(overlayRef), { destroy });
    } else if (context != null) {
      childContext = Object.assign({}, context, { destroy });
    } else {
      childContext = { destroy } as any;
    }

    const viewRef: EmbeddedViewRef<OverlayContext<C>> =
      viewContainerRef.createEmbeddedView(templateRef, childContext);

    viewRef.rootNodes.forEach((node) => {
      elem.appendChild(node);
    });

    overlayRef.viewRef = viewRef;
    this.overlayRefs.push(overlayRef);
    overlayRef.setDestroyFn(() => {
      let i: number;

      i = this.overlayRefs.indexOf(overlayRef);
      if (!!~i) {
        this.overlayRefs.splice(i, 1);
      }

      i = viewContainerRef.indexOf(viewRef);
      if (!!~i) {
        viewContainerRef.remove(i);
      }
    });

    overlayRef.init();

    return overlayRef;
  }

  /**
   * @internal
   */
  destroy(): void {
    let overlayRef: OverlayRef | undefined = this.overlayRefs.pop();
    while (overlayRef != null) {
      overlayRef.destroy();
      overlayRef = this.overlayRefs.pop();
    }

    this.overlayContainerService.destroy();
  }

  /**
   * @internal
   */
  getOverlayRefs(): OverlayRef[] {
    return this.overlayRefs;
  }

  /**
   * @internal
   */
  getLastOverlayRef(): OverlayRef | undefined {
    return !!this.overlayRefs.length
      ? this.overlayRefs[this.overlayRefs.length - 1]
      : undefined;
  }

  private createOverlayElement(): HTMLDivElement {
    let element = this.documentRef.createElement("div");
    this.overlayContainerService.getElement().appendChild(element);
    return element;
  }

  private getComponentRootNode<C>(componentRef: ComponentRef<C>): HTMLElement {
    return (componentRef.hostView as EmbeddedViewRef<C>)
      .rootNodes[0] as HTMLElement;
  }
}

export function OVERLAY_PROVIDER_FACTORY(
  document: Document,
  overlayContainerService: OverlayContainerService,
  applicationRef: ApplicationRef,
  componentFactoryResolver: ComponentFactoryResolver,
  injector: Injector,
  ngZone: NgZone,
  parent?: OverlayService
): OverlayService {
  if (parent != null) {
    return parent;
  }
  return new OverlayService(
    document,
    overlayContainerService,
    applicationRef,
    componentFactoryResolver,
    injector,
    ngZone
  );
}

export const OVERLAY_PROVIDER: FactoryProvider = {
  provide: OverlayService,
  deps: [
    DOCUMENT,
    OverlayContainerService,
    ApplicationRef,
    ComponentFactoryResolver,
    Injector,
    NgZone,
    [new SkipSelf(), new Optional(), OverlayService]
  ],
  useFactory: OVERLAY_PROVIDER_FACTORY
};
